import React from 'react';
import styled, { css } from 'styled-components';
import {basicColor, font, padding, radius} from '../../../../styles/template';

type PropsType = {
    accent?: boolean
    disabled?: boolean | any
    line?: boolean
    transparent?: boolean
    middle?: boolean
    small?: boolean
    light?: boolean
    secondary?: boolean
    success?: boolean
    link?: boolean
    onClick?: any
    autoWidth?: boolean
    type?: any
    isLoading?: boolean
    children: React.ReactNode
    href?: string
    dotted?: boolean
    target?: string
    rel?: string
}

const Wrap = styled.button<PropsType>`
    color: ${basicColor.white};
    border-radius: ${radius.normal};
    padding: 0 ${padding.small};
    ${font.accentM};
    position: relative;
    width: 100%;
    height: 7rem;
    text-decoration: none;
    white-space: nowrap;
    text-align: center;
    border: none;
    -webkit-backface-visibility: hidden;
    -moz-osx-font-smoothing: grayscale;
    cursor: pointer;
    background: none;
    z-index: 1;
    max-width: 100%;
    display: flex;
    gap: 1rem;
    justify-items: center;
    align-items: center;
    justify-content: center;
    align-content: center;
    transition: all 0.3s ease;

    &:active {
        transform: translateY(0.2rem);
    }
  
    &:before, &:after {
        content: '';
        z-index: -1;
        border-radius: inherit;
        pointer-events: none;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        -webkit-backface-visibility: hidden;
        transition: opacity 0.3s, -webkit-transform .3s;
        transition: transform 0.3s, opacity .3s;
        transition: transform 0.3s, opacity 0.3s, -webkit-transform .3s;
        transition-timing-function: cubic-bezier(0.75, 0, 0.125, 1);
    }
  
    &:before {
        border-width: 3px;
        border-style: solid;
        opacity: 0;
        -webkit-transform: scale3d(1, 1, 1);
        transform: scale3d(1, 1, 1);
    }
    
    &:hover,
    &:focus {
        text-decoration: none;
        outline: none;
    }
    
    &:hover:before {
        opacity: 1;
        -webkit-transform: scale3d(1, 1, 1);
        transform: scale3d(1, 1, 1);
    }
    
    &:hover:after {
        opacity: 0;
        -webkit-transform: scale3d(0.8, 0.8, 1);
        transform: scale3d(0.8, 0.8, 1);
    }
    
    &:before {
        border-color: ${basicColor.primary};
    }
    
    &:after {
        background-color: ${basicColor.primary};
    }
  
    &:hover {
        color: ${basicColor.primary};
        & .colorFill {
            fill: ${basicColor.primary};
        }
        & .colorStroke {
            stroke: ${basicColor.primary};
        }
    }
    
    & .colorFill {
        fill: ${basicColor.white};
    }
    
    & .colorStroke {
        stroke: ${basicColor.white};
    }

    ${({ autoWidth }) => autoWidth && css`
         width: fit-content;
    `}

    ${({ theme, disabled }) => disabled && css`
        color: ${theme.color.MidtoneItem} !important;
      
        & .colorFill {
            fill: ${theme.color.MidtoneItem} !important;
        }
      
        & .colorStroke {
            stroke: ${theme.color.MidtoneItem} !important;
        }
        
        &:before {
            border: none;
        }
        
        &:after {
            background-color: ${theme.title === 'dark' ? theme.color.Item : theme.color.LightItem} !important;
        }
        
        &:hover {
            color: ${theme.color.MidtoneItem} !important;
            & .colorFill {
                fill: ${theme.color.MidtoneItem} !important;
            }
            & .colorStroke {
                stroke: ${theme.color.MidtoneItem} !important;
            }
        }   
        
        &:before, &:after, &:hover:before, &:hover:after {
            transition: none;
            transform: none;
            opacity: 1;
        }
    `}

    ${({ accent }) => accent && css`
        &:before {
            border-color: ${basicColor.red};
        }
        
        &:after {
            background-color: ${basicColor.red};
        }
        
        &:hover {
            color: ${basicColor.red};
            & .colorFill {
                fill: ${basicColor.red};
            }
            & .colorStroke {
                stroke: ${basicColor.red};
            }
        }               
    `}
    
    ${({ line }) => line && css`
      border: 3px solid ${basicColor.white};
      
        &:before {
            border: none;
        }
        
        &:after {
            background: none;
        }
        
        &:hover {
          opacity: 0.5;
            color: ${basicColor.white};
            & .colorFill {
                fill: ${basicColor.white};
            }
            & .colorStroke {
                stroke: ${basicColor.white};
            }
        }               
    `}

    ${({ transparent }) => transparent && css`
        &:before {
            border-color: ${basicColor.white};
        }
        
        &:after {
            background-color: ${basicColor.transparent};
        }
        
        &:hover {
            color: ${basicColor.white};
            & .colorFill {
                fill: ${basicColor.white};
            }
            & .colorStroke {
                stroke: ${basicColor.white};
            }
        }               
    `}
    
    ${({ theme, secondary }) => secondary && css`
        &:before {
            border-color:  ${theme.color.Text};
        }
        
        &:after {
            background-color:  ${theme.title === 'dark' ? theme.color.Item : theme.color.Text};
        }
        
        &:hover {
            color:  ${theme.color.Text};
            & .colorFill {
                fill:  ${theme.color.Text};
            }
            & .colorStroke {
                stroke:  ${theme.color.Text};
            }
        }               
    `}

    ${({ light, success }) => (light || success )&& css`
        &:before {
            border:  none;
        }
        
        &:hover {
            color:  ${basicColor.white};
            & .colorFill {
                fill:  ${basicColor.white};
            }
            & .colorStroke {
                stroke:  ${basicColor.white};
            }
        }               
    `}
    

    ${({ light }) => light && css`
        color:  ${basicColor.primary};

        & .colorFill {
            fill:  ${basicColor.primary};
        }
      
        & .colorStroke {
            stroke:  ${basicColor.primary};
        }
        
        &:after {
            background-color:  ${basicColor.primaryTransparent};
        }
        
        &:hover {
            background-color:  ${basicColor.primary};
        }               
    `}

    ${({ success }) => success && css`
        color: ${basicColor.secondaryDark};
        
        & .colorFill {
            fill: ${basicColor.secondaryDark};
        }
        & .colorStroke {
            stroke: ${basicColor.secondaryDark};
        }
        
        &:after {
            background-color: ${basicColor.secondaryTransparent};
        }
        
        &:hover {
            background-color: ${basicColor.secondary};
        }               
    `}
    
    

    ${({ middle }) => middle && css`
        height: 6rem;
        gap: 1rem;
        padding: 0 2rem;
    `}

    ${({ small }) => small && css`
        font-size: 2rem;
        height: 5rem;
        gap: 0.5rem;
        padding: 0 1rem;
    `}

    ${({ link }) => link && css`
        color: ${basicColor.primary};
      
        & .colorFill {
            fill: ${basicColor.primary};
        }
      
        & .colorStroke {
            stroke: ${basicColor.primary};
        }
      
        &:before {
            border-color: transparent;
        }
        
        &:after {
            background-color: transparent;
        }
      
        &:hover {
            color: ${basicColor.primaryLight};
            & .colorFill {
                fill: ${basicColor.primaryLight};
            }
            & .colorStroke {
                stroke: ${basicColor.primaryLight};
            }
        }               
    `}
    
    
    ${({ dotted, theme }) => dotted && css`
        border: 2px dotted ${theme.color.LightItem};
        color: ${basicColor.primary};
      
        & .colorFill {
            fill: ${basicColor.primary};
        }
      
        & .colorStroke {
            stroke: ${basicColor.primary};
        }
      
        &:before {
            border-color: transparent;
        }
        
        &:after {
            background-color: transparent;
        }
      
        &:hover {
            color: ${basicColor.primaryLight};
            & .colorFill {
                fill: ${basicColor.primaryLight};
            }
            & .colorStroke {
                stroke: ${basicColor.primaryLight};
            }
        }               
    `}
`;

const InnerWrap = styled.p`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: fit-content;
    max-width: 100%;
    margin: 0; 
    line-height: 1; 
`;

const Button: React.FC<PropsType> = props => {
    const { children, href, type = 'button', ...restProps } = props;

    const map = React.Children.map(children, (child) => {
        if (typeof child === 'string') {
            return <InnerWrap>{child}</InnerWrap>
        } else return <>{child}</>
    })

    if (href) {
        return (
            <Wrap as="a" href={href} {...restProps}>{map}</Wrap>
        );
    }

    return <Wrap type={type} {...restProps}>{map}</Wrap>

}

export default Button;
